// Recharts area chart: baseline plan vs predictive forecast with confidence band.
// data: [{ period, baseline, predicted, ciLow, ciHigh, anomaly?: boolean }]

import {
  ResponsiveContainer,
  Area,
  Line,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceDot,
} from 'recharts';
import './charts.css';
import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

export function PredictiveSlippage({
  data = [],
  height = 280,
  baselineLabel = 'Planned',
  predictedLabel = 'Predicted',
  unit = 'days',
  surface = 'predictive-slippage',
}) {
  const tooltip = useOptionalRichTooltipController();
  const anomaly = data.find((d) => d.anomaly);
  return (
    <div className="predictive-slippage" role="img" aria-label="Predictive slippage curve">
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={data} margin={{ top: 16, right: 16, bottom: 8, left: 0 }}>
          <defs>
            <linearGradient id="slippage-fill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1961a1" stopOpacity={0.32} />
              <stop offset="100%" stopColor="#1961a1" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#e1e4ea" vertical={false} />
          <XAxis
            dataKey="period"
            stroke="#5a5e66"
            tickLine={false}
            tick={{ fontSize: 11 }}
            axisLine={{ stroke: '#c4c7cf' }}
          />
          <YAxis
            stroke="#5a5e66"
            tickLine={false}
            axisLine={false}
            tick={{ fontSize: 11 }}
            tickFormatter={(v) => `${v}${unit === '%' ? '%' : ''}`}
            width={40}
          />
          <Area
            type="monotone"
            dataKey="predicted"
            stroke="#1961a1"
            strokeWidth={2.2}
            strokeDasharray="6 4"
            fill="url(#slippage-fill)"
            name={predictedLabel}
            isAnimationActive
            animationDuration={700}
            dot={(props) => {
              const point = props.payload;
              const triggerProps =
                point?.richTooltip && tooltip
                  ? tooltip.getTriggerProps({
                      surfaceId: point.richTooltip.surfaceId,
                      payload: point.richTooltip,
                      testIdPrefix: 'rich-tooltip',
                      ariaLabel: `${surface} ${point.period} predicted ${point.predicted} ${unit}`,
                    })
                  : {};
              return (
                <circle
                  {...triggerProps}
                  className="rich-tooltip-svg-trigger predictive-point-trigger"
                  data-testid={`predictive-point-${surface}-${point.period}`}
                  cx={props.cx}
                  cy={props.cy}
                  r={4.5}
                  fill="#1961a1"
                  stroke="#fff"
                  strokeWidth={2}
                  tabIndex={point?.richTooltip ? 0 : undefined}
                  role={point?.richTooltip ? 'button' : undefined}
                />
              );
            }}
            activeDot={false}
          />
          <Line
            type="monotone"
            dataKey="baseline"
            stroke="#00152c"
            strokeWidth={2}
            dot={false}
            name={baselineLabel}
            isAnimationActive
            animationDuration={700}
          />
          {anomaly ? (
            <ReferenceDot
              x={anomaly.period}
              y={anomaly.predicted}
              r={6}
              fill="#c0524d"
              stroke="#fff"
              strokeWidth={2}
            />
          ) : null}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
